import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, LogOut, ChevronDown, RefreshCw, ListFilter, MessageCircleQuestionMark, EllipsisVertical } from 'lucide-react';

const tabs = [
  { path: '/geral', label: 'Visão geral', shortLabel: 'Visão geral' },
  { path: '/vendas', label: 'Evo. Vendas', shortLabel: 'Evo. Vendas' },
];

const Navigation = ({
  onToggleSidebar,
  isSidebarOpen,
  showFilters,
  onToggleFilters,
  lastUpdate,
  onRefresh
}) => {
  const location = useLocation();
  const { user, getUserInitials, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [timeAgo, setTimeAgo] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Detectar tamanho da tela
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calcular tempo desde última atualização
  useEffect(() => {
    const calcularTempoPassado = () => {
      if (!lastUpdate) {
        setTimeAgo('');
        return;
      }

      const agora = new Date();
      const diff = Math.floor((agora - lastUpdate) / 1000); // diferença em segundos

      if (diff < 60) {
        setTimeAgo('Atualizado agora');
      } else if (diff < 3600) {
        const minutos = Math.floor(diff / 60);
        setTimeAgo(`Atualizado há ${minutos} min`);
      } else if (diff < 86400) {
        const horas = Math.floor(diff / 3600);
        setTimeAgo(`Atualizado há ${horas}h`);
      } else {
        const dias = Math.floor(diff / 86400);
        setTimeAgo(`Atualizado há ${dias}d`);
      }
    };

    calcularTempoPassado();
    const interval = setInterval(calcularTempoPassado, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(interval);
  }, [lastUpdate]);

  // Auto-refresh a cada 30 minutos
  useEffect(() => {
    if (!onRefresh) return;

    // Intervalo de 30 minutos (1800000 milissegundos)
    const autoRefreshInterval = setInterval(() => {
      if (onRefresh) {
        onRefresh();
      }
    }, 30 * 60 * 1000); // 30 minutos

    return () => clearInterval(autoRefreshInterval);
  }, [onRefresh]);

  const getRoleColor = (role) => {
    if (!role) return 'from-gray-400 to-gray-500';
    const roleLower = role.toLowerCase();
    if (roleLower === 'admin') return 'from-blue-900 to-blue-900';
    if (roleLower === 'manager') return 'from-blue-500 to-blue-600';
    if (roleLower === 'user') return 'from-green-500 to-green-600';
    return 'from-orange-500 to-orange-600';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-white shadow-sm px-2 sm:px-4 md:px-8 lg:px-14">
      <div className="mx-auto flex items-center justify-between px-1 sm:px-2 md:px-4 lg:px-6 py-2 h-12 sm:h-14">
        {/* Lado esquerdo: botão menu + logo */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-7 flex-shrink-0">
          {/* Botão abrir sidebar */}
          {!isSidebarOpen && (
            <button
              type="button"
              onClick={onToggleSidebar}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center shadow-sm border border-orange-500 hover:bg-orange-50 transition-colors flex-shrink-0"
              title="Abrir menu"
            >
              <Menu className="w-4 h-4 text-[#f09005]" />
            </button>
          )}

          {/* Logo Mucuripe - esconde em mobile pequeno */}
          <div className="hidden sm:flex items-center flex-shrink-0">
            <img src="images/logo-mucuripe.png" alt="Logo Mucuripe" className="h-8 md:h-10 lg:h-12 w-auto" />
          </div>
        </div>

        {/* Centro: abas */}
        <div className="flex-1 flex justify-center px-1 sm:px-2">
          <div className="flex items-center bg-[#fbf1e6] rounded-full px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 gap-1.5 sm:gap-2 md:gap-3 lg:gap-5">
            {tabs.map((tab) => {
              const active = location.pathname === tab.path;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`
                    h-4 sm:h-5 lg:h-6
                    px-2 sm:px-3 md:px-4
                    py-0.5
                    text-xs sm:text-xs md:text-sm
                    font-medium rounded-full transition-colors whitespace-nowrap flex items-center justify-center
                    ${active
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-[#f09005] hover:bg-orange-200'
                    }
                  `}
                >
                  {isMobile ? tab.shortLabel : tab.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Lado direito: indicador de atualização + botão filtros + menu do usuário */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0">

          {/* Indicador de última atualização */}
          {lastUpdate && (
            <>
              {/* Versão desktop: ícone + texto */}
              <button
                type="button"
                onClick={onRefresh}
                className="hidden p-1 md:flex items-center gap-1 sm:gap-1.5 bg-[#fcf5ed] rounded-full transition-colors hover:bg-orange-100 focus:outline-none"
                title="Atualizar agora"
              >
                <div className="w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-[#e97608]">
                  <RefreshCw className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                </div>
                <span className="text-[10px] sm:text-sm text-[#6c6b6c] font-normal tracking-wide truncate max-w-[60px] sm:max-w-none">
                  {timeAgo}
                </span>
              </button>
              {/* Versão mobile: só o ícone */}
              <button
                type="button"
                onClick={onRefresh}
                className="flex md:hidden items-center justify-center bg-[#fcf5ed] rounded-full transition-colors hover:bg-orange-100 focus:outline-none w-7 h-7"
                title="Atualizar agora"
              >
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#e97608]">
                  <RefreshCw className="w-3 h-3 text-white" />
                </div>
              </button>
            </>
          )}

          {/* Botão Toggle Filtros */}
          <button
            type="button"
            onClick={onToggleFilters}
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all flex-shrink-0 ${showFilters
              ? 'bg-orange-500 text-white'
              : 'bg-white text-[#f09005] hover:bg-orange-50 border border-[#f09005]'
              }`}
            title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          >
            <ListFilter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>

          {/* Menu do usuário */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {/* Avatar */}
              <div className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-r ${getRoleColor(user?.role)} overflow-hidden shadow-md flex items-center justify-center border-2 border-white`}>
                <span className="text-[9px] sm:text-[10px] md:text-[11px] font-semibold text-white">
                  {getUserInitials()}
                </span>
              </div>
              <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-500 transition-transform hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                {/* Overlay para fechar ao clicar fora */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />

                <div className="absolute right-0 mt-2 w-52 sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {/* Info do usuário */}
                  <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-100">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${getRoleColor(user?.role)} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0`}>
                        <span className="text-[10px] sm:text-xs">{getUserInitials()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {user?.name || 'Usuário'}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {user?.email || ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Perfil/Role */}
                  <div className="px-3 sm:px-4 py-2 border-b border-gray-100">
                    <div className="text-[10px] sm:text-xs text-gray-500 mb-1">Perfil:</div>
                    <span className="text-[10px] sm:text-xs bg-blue-100 text-blue-600 px-2 py-0.5 sm:py-1 rounded-full">
                      {user?.role || 'Usuário'}
                    </span>
                  </div>

                  {/* Ações */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                      }}
                      className="w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>Sair</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
